import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown } from 'lucide-react';
import { Tag, type TagTone } from '../ui/kit';
import {
  POPOVER,
  TRIGGER_CONTENT,
  triggerClass,
  useDropUp,
  type NotionVariant,
} from './notionControl';

export interface NotionSelectOption {
  value: string | number;
  label: string;
  /**
   * Render this option as the kit's status Tag, in this tone.
   *
   * It used to be a raw hex colour mixed into a bespoke chip at 15%/30% alpha,
   * which is how the same booking ended up showing `● PENDING` in the kit's
   * monospace Tag at the top of the drawer and a sentence-case amber `Pending`
   * three fields below it. One chip design, defined in one place.
   */
  tone?: TagTone;
}

interface NotionSelectProps {
  value: string | number;
  onChange: (value: any) => void;
  options: NotionSelectOption[];
  className?: string;
  /** Show the selection as a status Tag rather than plain text. */
  showAsTag?: boolean;
  variant?: NotionVariant;
}

/** Stand-in for a tone in the option list, where a full Tag would be noisy. */
const TONE_DOT: Record<TagTone, string> = {
  neutral: 'bg-zinc-400',
  live: 'bg-emerald-600',
  warn: 'bg-amber-500',
  danger: 'bg-red-600',
  info: 'bg-sky-600',
  violet: 'bg-violet-600',
  ink: 'bg-primary',
};

export function NotionSelect({
  value,
  onChange,
  options,
  className = '',
  showAsTag = false,
  variant = 'field',
}: NotionSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedOption = options.find(opt => opt.value === value);
  // Menu rows are 36px; the list is short enough that it never scrolls.
  const dropUp = useDropUp(isOpen, triggerRef, options.length * 36 + 16);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    // Stop the key at the select: a drawer listens for Escape too, and without
    // this the first press closes the whole panel instead of the menu.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen]);

  const handleSelect = (optionValue: string | number) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={variant === 'field' ? 'relative block w-full' : 'relative inline-block'}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(open => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={triggerClass(variant, className)}
      >
        <span className={TRIGGER_CONTENT}>
          {showAsTag && selectedOption ? (
            <Tag tone={selectedOption.tone ?? 'neutral'} dot>
              {selectedOption.label}
            </Tag>
          ) : (
            <span className="truncate">{selectedOption?.label ?? value}</span>
          )}
        </span>
        <ChevronDown size={15} className="shrink-0 text-zinc-400" aria-hidden />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            role="listbox"
            initial={{ opacity: 0, y: dropUp ? 6 : -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: dropUp ? 6 : -6 }}
            transition={{ duration: 0.15 }}
            className={`absolute z-50 py-1 ${POPOVER} ${
              variant === 'field' ? 'w-full' : 'min-w-[11rem]'
            } ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}
          >
            {options.map(option => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(option.value)}
                  className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-[14px] transition-colors hover:bg-paper-tint ${
                    isSelected ? 'font-medium text-primary' : 'text-zinc-600'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {option.tone && (
                      <span
                        className={`h-1.5 w-1.5 shrink-0 rounded-full ${TONE_DOT[option.tone]}`}
                      />
                    )}
                    {option.label}
                  </span>
                  {isSelected && <Check size={15} className="shrink-0 text-primary" />}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
